import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DataTexture,
  FloatType,
  Matrix4,
  MeshPhysicalMaterial,
  MeshPhysicalMaterialParameters,
  RGBAFormat,
  Vector3
} from 'three';

const JAW_HIGHLIGHT_TINT = new Vector3(1.0, 0.785, 0.227); // 0xffc83a
const JAW_HIGHLIGHT_EMISSIVE = 0.45;

/** MeshPhysicalMaterial with per-face color (data texture) + jaw-region tint. */
export class PerFaceMaterial extends MeshPhysicalMaterial {
  private faceColorTexture: DataTexture;
  private faceColorData: Float32Array;
  private faceCount: number;
  private faceIdToIndex: Map<string, number>;
  private defaultColor: Color;

  // Jaw-region highlight: shader tests each fragment against the unit cube in
  // each jaw's local space (using these inverse-world matrices) and paints
  // hits with jawHighlightTint.
  private jawHighlightFixedInv = new Matrix4();
  private jawHighlightMovableInv = new Matrix4();
  private jawHighlightActive = 0;
  private jawHighlightTint = new Vector3().copy(JAW_HIGHLIGHT_TINT);
  private jawHighlightEmissive = JAW_HIGHLIGHT_EMISSIVE;
  // Captured at first compile so setJawHighlight can update live uniforms
  // without recompiling.
  private compiledShader?: { uniforms: Record<string, { value: unknown }> };

  constructor(
    params: MeshPhysicalMaterialParameters,
    faceIds: string[],
    defaultColor: Color | number | string
  ) {
    super(params);

    this.faceCount = faceIds.length;
    this.defaultColor = new Color(defaultColor);
    this.faceIdToIndex = new Map();

    // Create mapping from faceId to index
    faceIds.forEach((id, index) => {
      this.faceIdToIndex.set(id, index);
    });

    // Create texture data: RGBA for each face
    // R, G, B = color, A = emissive intensity
    // Use power of 2 texture width for better GPU compatibility
    const textureWidth = Math.max(1, Math.ceil(Math.sqrt(this.faceCount)));
    const textureHeight = Math.ceil(this.faceCount / textureWidth);
    const textureSize = textureWidth * textureHeight;

    this.faceColorData = new Float32Array(textureSize * 4);

    // Initialize all faces with default color
    for (let i = 0; i < this.faceCount; i++) {
      const offset = i * 4;
      this.faceColorData[offset] = this.defaultColor.r;
      this.faceColorData[offset + 1] = this.defaultColor.g;
      this.faceColorData[offset + 2] = this.defaultColor.b;
      this.faceColorData[offset + 3] = 0; // No emissive by default
    }

    // Create the texture
    this.faceColorTexture = new DataTexture(
      this.faceColorData,
      textureWidth,
      textureHeight,
      RGBAFormat,
      FloatType
    );
    this.faceColorTexture.needsUpdate = true;

    // Store texture dimensions as uniforms
    const textureWidthUniform = textureWidth;
    const textureHeightUniform = textureHeight;

    this.onBeforeCompile = (shader) => {
      shader.uniforms['faceColorMap'] = { value: this.faceColorTexture };
      shader.uniforms['faceColorMapSize'] = { value: [textureWidthUniform, textureHeightUniform] };
      shader.uniforms['jawFixedWorldInv'] = { value: this.jawHighlightFixedInv };
      shader.uniforms['jawMovableWorldInv'] = { value: this.jawHighlightMovableInv };
      shader.uniforms['jawHighlightActive'] = { value: this.jawHighlightActive };
      shader.uniforms['jawHighlightTint'] = { value: this.jawHighlightTint };
      shader.uniforms['jawHighlightEmissive'] = { value: this.jawHighlightEmissive };
      this.compiledShader = shader as unknown as { uniforms: Record<string, { value: unknown }> };

      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `#include <common>
        attribute float faceIndex;
        varying float vFaceIndex;
        varying vec3 vJawWorldPos;
        `
      );

      // World position from raw `position` is safe: these meshes never
      // carry morph targets or skinning.
      shader.vertexShader = shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vFaceIndex = faceIndex;
        vJawWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
        `
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `#include <common>
        uniform sampler2D faceColorMap;
        uniform vec2 faceColorMapSize;
        uniform mat4 jawFixedWorldInv;
        uniform mat4 jawMovableWorldInv;
        uniform float jawHighlightActive;
        uniform vec3 jawHighlightTint;
        uniform float jawHighlightEmissive;
        varying float vFaceIndex;
        varying vec3 vJawWorldPos;

        vec4 getFaceColor(float faceIdx) {
          float idx = floor(faceIdx + 0.5);
          float x = mod(idx, faceColorMapSize.x) + 0.5;
          float y = floor(idx / faceColorMapSize.x) + 0.5;
          vec2 uv = vec2(x / faceColorMapSize.x, y / faceColorMapSize.y);
          return texture2D(faceColorMap, uv);
        }

        // The jaw geometry is BoxGeometry(1,1,1) ⇒ unit cube [-0.5, 0.5]^3.
        bool jawInsideUnitCube(vec3 p) {
          vec3 a = step(vec3(-0.5), p);
          vec3 b = step(p, vec3(0.5));
          return (a.x * a.y * a.z * b.x * b.y * b.z) > 0.5;
        }
        `
      );

      // Compute membership once and reuse in both color + emissive.
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <color_fragment>',
        `bool jawHighlightHit = false;
        if (jawHighlightActive > 0.5) {
          vec3 pFixed = (jawFixedWorldInv * vec4(vJawWorldPos, 1.0)).xyz;
          vec3 pMov   = (jawMovableWorldInv * vec4(vJawWorldPos, 1.0)).xyz;
          jawHighlightHit = jawInsideUnitCube(pFixed) || jawInsideUnitCube(pMov);
        }
        #include <color_fragment>
        vec4 faceColorData = getFaceColor(vFaceIndex);
        diffuseColor.rgb = jawHighlightHit ? jawHighlightTint : faceColorData.rgb;
        `
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        vec4 faceEmissiveData = getFaceColor(vFaceIndex);
        totalEmissiveRadiance += faceEmissiveData.rgb * faceEmissiveData.a * 0.5;
        if (jawHighlightHit) {
          totalEmissiveRadiance += jawHighlightTint * jawHighlightEmissive;
        }
        `
      );
    };
  }

  /**
   * Enable/update the jaw-region highlight. Pass `jawMesh.matrixWorld.invert()`
   * for each jaw, or `null` to disable. Matrices are copied — caller can reuse.
   */
  setJawHighlight(fixedInv: Matrix4 | null, movableInv: Matrix4 | null): void {
    if (fixedInv) this.jawHighlightFixedInv.copy(fixedInv);
    if (movableInv) this.jawHighlightMovableInv.copy(movableInv);
    this.jawHighlightActive = (fixedInv || movableInv) ? 1 : 0;
    if (this.compiledShader) {
      this.compiledShader.uniforms['jawHighlightActive'].value = this.jawHighlightActive;
    }
  }

  /**
   * Set the color of a specific face
   */
  setFaceColor(faceId: string, color: Color | number | string): void {
    const index = this.faceIdToIndex.get(faceId);
    if (index === undefined) return;

    const c = new Color(color);
    const offset = index * 4;
    this.faceColorData[offset] = c.r;
    this.faceColorData[offset + 1] = c.g;
    this.faceColorData[offset + 2] = c.b;
    if (!this.batchMode) this.faceColorTexture.needsUpdate = true;
  }

  /**
   * Set the emissive intensity of a specific face
   */
  setFaceEmissive(faceId: string, intensity: number): void {
    const index = this.faceIdToIndex.get(faceId);
    if (index === undefined) return;

    const offset = index * 4;
    this.faceColorData[offset + 3] = intensity;
    if (!this.batchMode) this.faceColorTexture.needsUpdate = true;
  }

  /**
   * Set both color and emissive for a face
   */
  setFaceState(faceId: string, color: Color | number | string, emissive: number): void {
    const index = this.faceIdToIndex.get(faceId);
    if (index === undefined) return;

    const c = new Color(color);
    const offset = index * 4;
    this.faceColorData[offset] = c.r;
    this.faceColorData[offset + 1] = c.g;
    this.faceColorData[offset + 2] = c.b;
    this.faceColorData[offset + 3] = emissive;
    if (!this.batchMode) this.faceColorTexture.needsUpdate = true;
  }

  /**
   * Reset a face to the default color
   */
  resetFace(faceId: string): void {
    const index = this.faceIdToIndex.get(faceId);
    if (index === undefined) return;

    const offset = index * 4;
    this.faceColorData[offset] = this.defaultColor.r;
    this.faceColorData[offset + 1] = this.defaultColor.g;
    this.faceColorData[offset + 2] = this.defaultColor.b;
    this.faceColorData[offset + 3] = 0;
    if (!this.batchMode) this.faceColorTexture.needsUpdate = true;
  }

  /**
   * Reset all faces to default color
   */
  resetAllFaces(): void {
    for (let i = 0; i < this.faceCount; i++) {
      const offset = i * 4;
      this.faceColorData[offset] = this.defaultColor.r;
      this.faceColorData[offset + 1] = this.defaultColor.g;
      this.faceColorData[offset + 2] = this.defaultColor.b;
      this.faceColorData[offset + 3] = 0;
    }
    this.faceColorTexture.needsUpdate = true;
  }

  /**
   * Set color for multiple faces at once (batched - single texture update)
   */
  setFacesColor(faceIds: string[], color: Color | number | string): void {
    const c = new Color(color);
    for (const faceId of faceIds) {
      const index = this.faceIdToIndex.get(faceId);
      if (index === undefined) continue;

      const offset = index * 4;
      this.faceColorData[offset] = c.r;
      this.faceColorData[offset + 1] = c.g;
      this.faceColorData[offset + 2] = c.b;
    }
    this.faceColorTexture.needsUpdate = true;
  }

  /**
   * Set emissive for multiple faces at once (batched - single texture update)
   */
  setFacesEmissive(faceIds: string[], intensity: number): void {
    for (const faceId of faceIds) {
      const index = this.faceIdToIndex.get(faceId);
      if (index === undefined) continue;

      const offset = index * 4 + 3;
      this.faceColorData[offset] = intensity;
    }
    this.faceColorTexture.needsUpdate = true;
  }

  /**
   * Begin a batch update - call this before multiple setFaceColor/setFaceEmissive calls
   * Then call endBatchUpdate() when done to update the texture once
   */
  private batchMode = false;
  
  beginBatchUpdate(): void {
    this.batchMode = true;
  }

  /**
   * End a batch update and trigger texture update
   */
  endBatchUpdate(): void {
    this.batchMode = false;
    this.faceColorTexture.needsUpdate = true;
  }

  /**
   * Dispose of the material and texture
   */
  override dispose(): void {
    this.faceColorTexture.dispose();
    super.dispose();
  }
}

/**
 * Merges multiple BufferGeometries into one, adding a faceIndex attribute
 * Each vertex in the merged geometry will have a faceIndex indicating which original geometry it came from
 */
export function mergeGeometriesWithFaceIndex(
  geometries: BufferGeometry[],
  faceIds: string[]
): { geometry: BufferGeometry; faceIdToIndex: Map<string, number> } {
  if (geometries.length === 0) {
    return { geometry: new BufferGeometry(), faceIdToIndex: new Map() };
  }

  if (geometries.length !== faceIds.length) {
    throw new Error('geometries and faceIds must have the same length');
  }

  // Calculate total vertex count and index count
  // For non-indexed geometries, we create indices (every 3 vertices = 1 triangle)
  let totalVertices = 0;
  let totalIndices = 0;
  for (const geo of geometries) {
    const posAttr = geo.attributes['position'];
    if (!posAttr) continue;
    
    totalVertices += posAttr.count;
    if (geo.index) {
      totalIndices += geo.index.count;
    } else {
      // Non-indexed: each 3 vertices form a triangle
      totalIndices += posAttr.count;
    }
  }

  // Create merged arrays
  const mergedPositions = new Float32Array(totalVertices * 3);
  const mergedNormals = new Float32Array(totalVertices * 3);
  const mergedFaceIndices = new Float32Array(totalVertices);
  const mergedIndices = new Uint32Array(totalIndices);

  const faceIdToIndex = new Map<string, number>();

  let vertexOffset = 0;
  let indexOffset = 0;

  for (let i = 0; i < geometries.length; i++) {
    const geo = geometries[i];
    const faceId = faceIds[i];
    faceIdToIndex.set(faceId, i);

    const positions = geo.attributes['position'];
    if (!positions) continue;
    
    const normals = geo.attributes['normal'];

    // Copy positions
    for (let v = 0; v < positions.count; v++) {
      const idx = (vertexOffset + v) * 3;
      mergedPositions[idx] = positions.getX(v);
      mergedPositions[idx + 1] = positions.getY(v);
      mergedPositions[idx + 2] = positions.getZ(v);

      // Copy normals
      if (normals) {
        mergedNormals[idx] = normals.getX(v);
        mergedNormals[idx + 1] = normals.getY(v);
        mergedNormals[idx + 2] = normals.getZ(v);
      }

      // Set face index - this maps each vertex to its original BREP face
      mergedFaceIndices[vertexOffset + v] = i;
    }

    // Copy indices (offset by vertex count)
    if (geo.index) {
      for (let j = 0; j < geo.index.count; j++) {
        mergedIndices[indexOffset + j] = geo.index.getX(j) + vertexOffset;
      }
      indexOffset += geo.index.count;
    } else {
      // Non-indexed geometry: create sequential indices
      for (let v = 0; v < positions.count; v++) {
        mergedIndices[indexOffset + v] = vertexOffset + v;
      }
      indexOffset += positions.count;
    }

    vertexOffset += positions.count;
  }

  // Create merged geometry
  const mergedGeometry = new BufferGeometry();
  mergedGeometry.setAttribute('position', new BufferAttribute(mergedPositions, 3));
  mergedGeometry.setAttribute('normal', new BufferAttribute(mergedNormals, 3));
  mergedGeometry.setAttribute('faceIndex', new BufferAttribute(mergedFaceIndices, 1));
  mergedGeometry.setIndex(new BufferAttribute(mergedIndices, 1));

  mergedGeometry.computeBoundingSphere();
  mergedGeometry.computeBoundingBox();

  return { geometry: mergedGeometry, faceIdToIndex };
}

/**
 * Extracts geometry for specific face IDs from a merged mesh
 * Returns an array of BufferGeometry objects, one for each requested faceId
 */
export function extractFaceGeometries(
  mergedGeometry: BufferGeometry,
  faceIdToIndex: Map<string, number>,
  faceIds: string[]
): BufferGeometry[] {
  const result: BufferGeometry[] = [];

  const positions = mergedGeometry.attributes['position'] as BufferAttribute;
  const normals = mergedGeometry.attributes['normal'] as BufferAttribute;
  const faceIndexAttr = mergedGeometry.attributes['faceIndex'] as BufferAttribute;
  const indices = mergedGeometry.index;

  if (!positions || !faceIndexAttr) {
    return result;
  }

  for (const faceId of faceIds) {
    const targetFaceIndex = faceIdToIndex.get(faceId);
    if (targetFaceIndex === undefined) {
      continue;
    }

    // Find all triangles that belong to this face
    const facePositions: number[] = [];
    const faceNormals: number[] = [];

    if (indices) {
      // Indexed geometry: iterate through triangles
      for (let i = 0; i < indices.count; i += 3) {
        const i0 = indices.getX(i);
        const i1 = indices.getX(i + 1);
        const i2 = indices.getX(i + 2);

        // Check if this triangle belongs to the target face
        // All vertices of a triangle should have the same faceIndex
        const fi0 = faceIndexAttr.getX(i0);
        if (fi0 !== targetFaceIndex) continue;

        // Add the triangle vertices
        facePositions.push(
          positions.getX(i0), positions.getY(i0), positions.getZ(i0),
          positions.getX(i1), positions.getY(i1), positions.getZ(i1),
          positions.getX(i2), positions.getY(i2), positions.getZ(i2)
        );

        if (normals) {
          faceNormals.push(
            normals.getX(i0), normals.getY(i0), normals.getZ(i0),
            normals.getX(i1), normals.getY(i1), normals.getZ(i1),
            normals.getX(i2), normals.getY(i2), normals.getZ(i2)
          );
        }
      }
    } else {
      // Non-indexed geometry: every 3 vertices form a triangle
      for (let i = 0; i < positions.count; i += 3) {
        const fi = faceIndexAttr.getX(i);
        if (fi !== targetFaceIndex) continue;

        facePositions.push(
          positions.getX(i), positions.getY(i), positions.getZ(i),
          positions.getX(i + 1), positions.getY(i + 1), positions.getZ(i + 1),
          positions.getX(i + 2), positions.getY(i + 2), positions.getZ(i + 2)
        );

        if (normals) {
          faceNormals.push(
            normals.getX(i), normals.getY(i), normals.getZ(i),
            normals.getX(i + 1), normals.getY(i + 1), normals.getZ(i + 1),
            normals.getX(i + 2), normals.getY(i + 2), normals.getZ(i + 2)
          );
        }
      }
    }

    if (facePositions.length > 0) {
      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(facePositions), 3));
      if (faceNormals.length > 0) {
        geometry.setAttribute('normal', new BufferAttribute(new Float32Array(faceNormals), 3));
      }
      geometry.computeBoundingSphere();
      geometry.computeBoundingBox();
      result.push(geometry);
    }
  }

  return result;
}

